import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Panel, TrendBadge, FactorChip, Stat } from "../components/common";
import { ScoreGauge, ScoreBar } from "../components/ScoreGauge";
import { scoreMeta, chgColor, pct, fmt, subScoreOrder, corrColor, classLabel } from "../lib/format";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, Cell, ReferenceLine,
} from "recharts";
import { ArrowLeft, Activity, BarChart2, Sparkles, History, Layers, Gauge, GitCompareArrows } from "lucide-react";

const TABS = [["overview", "Overview"], ["scores", "Sub-Scores"], ["why", "WHY Score"], ["analogues", "Analogues"], ["corr", "Correlations"]];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Asset() {
  const { ticker } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [prices, setPrices] = useState([]);
  const [analogues, setAnalogues] = useState([]);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false); setTab("overview");
    api.get(`/assets/${ticker}`).then((r) => setA(r.data)).catch(() => setErr(true));
    api.get(`/assets/${ticker}/prices`, { params: { days: 260 } }).then((r) => setPrices(r.data.ohlc)).catch(() => {});
    api.get("/analogues").then((r) => setAnalogues(r.data)).catch(() => {});
  }, [ticker]);

  if (err) return <div className="font-mono text-sm text-[#EF4444]">Asset "{ticker}" not found. Try SPY, NVDA, BTC, GLD…</div>;
  if (!a) return <div className="font-mono text-sm text-slate-500 animate-pulse">loading {ticker}…</div>;

  const m = scoreMeta(a.smart_quant_score);
  const t = a.technical;
  const chartData = prices.map((p) => ({ t: p.t, c: p.c }));
  const radar = subScoreOrder.map(([k, l]) => ({ dim: l, v: a.scores[k] }));
  const seas = a.seasonality;
  const seasData = MONTHS.map((nm, i) => ({ m: nm, avg: seas.months[String(i + 1)]?.avg ?? 0, win: seas.months[String(i + 1)]?.win_rate ?? 50 }));

  return (
    <div className="space-y-5" data-testid="asset-page">
      <div className="flex items-center justify-between">
        <button data-testid="asset-back" onClick={() => nav(-1)} className="flex items-center gap-1.5 eyebrow text-slate-400 hover:text-[#00E5FF] transition-colors">
          <ArrowLeft size={13} /> back
        </button>
        <button data-testid="asset-compare-link" onClick={() => nav(`/compare?a=${a.symbol}&b=${a.symbol === "SPY" ? "QQQ" : "SPY"}`)}
          className="flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-md border border-[#334155] text-slate-300 hover:bg-[#00E5FF12] hover:border-[#00E5FF44] hover:text-[#00E5FF] transition-colors">
          <GitCompareArrows size={13} /> Compare
        </button>
      </div>

      {/* header */}
      <div className="panel p-5">
        <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-display text-4xl font-extrabold tracking-tight">{a.symbol}</h1>
              <span className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#334155] text-slate-300">{classLabel[a.asset_class]}</span>
              <span className="text-[11px] font-mono text-slate-500">{a.sector} · {a.country}</span>
            </div>
            <div className="text-slate-400 text-sm mt-0.5">{a.name}</div>
            <div className="flex items-end gap-4 mt-3">
              <span className="font-mono text-3xl font-extrabold tnum">{fmt(a.price)}</span>
              <span className="font-mono text-lg tnum mb-0.5" style={{ color: chgColor(a.change_1d) }}>{pct(a.change_1d)}</span>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-xs font-mono text-slate-400">
              <span>RSI <b className="text-slate-200">{fmt(t.rsi, 1)}</b></span>
              <span>Vol(1Y) <b className="text-slate-200">{fmt(t.realized_vol_1y, 1)}%</b></span>
              <span>Drawdown <b style={{ color: "#EF4444" }}>{fmt(t.drawdown, 1)}%</b></span>
              <span>from ATH <b style={{ color: "#EF4444" }}>{fmt(t.dist_ath, 1)}%</b></span>
              <span>MaxDD <b style={{ color: "#EF4444" }}>{fmt(t.max_drawdown, 1)}%</b></span>
            </div>
          </div>
          <div className="flex flex-col items-center" data-testid="smart-quant-gauge">
            <div className="eyebrow mb-2">Smart Quant Score</div>
            <ScoreGauge value={a.smart_quant_score} size={168} thickness={13} />
            <span className="mt-2 text-sm font-semibold px-3 py-1 rounded" style={{ color: m.color, background: `${m.color}18` }}>{a.classification}</span>
          </div>
        </div>
      </div>

      {/* trend dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="trend-dashboard">
        <TrendBadge trend={a.trends.short} label="Short Term (20/50)" testid="trend-short-term-badge" />
        <TrendBadge trend={a.trends.medium} label="Medium Term (50/100)" testid="trend-medium-term-badge" />
        <TrendBadge trend={a.trends.long} label="Long Term (100/200)" testid="trend-long-term-badge" />
      </div>

      {/* tabs */}
      <div className="flex gap-1.5 border-b border-[#1E293B]">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`asset-tab-${k}`} onClick={() => setTab(k)}
            className={`text-sm px-4 py-2 -mb-px border-b-2 transition-colors ${tab === k ? "border-[#00E5FF] text-[#00E5FF]" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-5">
          <Panel title="Price · Attention Bands" icon={<Activity size={13} />}
            right={<div className="flex gap-3 text-[10px] font-mono text-slate-400"><span>SMA200 {fmt(t.sma200)}</span><span>Support {fmt(t.support)}</span><span>Resist {fmt(t.resistance)}</span></div>}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="pc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={m.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={m.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1B243B" vertical={false} />
                <XAxis dataKey="t" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} minTickGap={60} />
                <YAxis domain={["auto", "auto"]} tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono" }} />
                <ReferenceLine y={t.attention_bands.upper_2} stroke="#EF4444" strokeDasharray="3 3" strokeOpacity={0.5} />
                <ReferenceLine y={t.attention_bands.lower_2} stroke="#10B981" strokeDasharray="3 3" strokeOpacity={0.5} />
                <Area type="monotone" dataKey="c" stroke={m.color} strokeWidth={2} fill="url(#pc)" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Panel title="Momentum & Technicals" icon={<BarChart2 size={13} />}>
              <div className="grid grid-cols-3 gap-4">
                <Stat label="Mom 1M" value={pct(t.mom_1m)} color={chgColor(t.mom_1m)} />
                <Stat label="Mom 3M" value={pct(t.mom_3m)} color={chgColor(t.mom_3m)} />
                <Stat label="Mom 6M" value={pct(t.mom_6m)} color={chgColor(t.mom_6m)} />
                <Stat label="Mom 12M" value={pct(t.mom_12m)} color={chgColor(t.mom_12m)} />
                <Stat label="MACD Hist" value={fmt(t.macd_hist, 2)} color={chgColor(t.macd_hist)} />
                <Stat label="ROC" value={pct(t.roc)} color={chgColor(t.roc)} />
                <Stat label="SMA50 slope" value={pct(t.sma50_slope)} color={chgColor(t.sma50_slope)} />
                <Stat label="Rel Volume" value={`${fmt(t.rel_volume, 2)}x`} />
                <Stat label="Breakout" value={t.breakout ? "YES" : "no"} color={t.breakout ? "#10B981" : "#94A3B8"} />
              </div>
            </Panel>
            {a.fundamentals ? (
              <Panel title="Fundamentals" icon={<Gauge size={13} />}
                right={<span data-testid="fundamentals-source-badge" className="text-[9px] font-mono px-2 py-0.5 rounded border"
                  style={a.fundamentals.source === "live"
                    ? { color: "#10B981", borderColor: "#10B98144", background: "#10B98112" }
                    : { color: "#64748B", borderColor: "#33415544" }}>
                  {a.fundamentals.source === "live" ? "● LIVE · Alpha Vantage" : "○ modeled"}
                </span>}>
                <div className="grid grid-cols-3 gap-4">
                  <Stat label="P/E" value={fmt(a.fundamentals.metrics.pe, 1)} />
                  <Stat label="Fwd P/E" value={fmt(a.fundamentals.metrics.forward_pe, 1)} />
                  <Stat label="PEG" value={fmt(a.fundamentals.metrics.peg, 2)} />
                  <Stat label="P/B" value={fmt(a.fundamentals.metrics.pb, 1)} />
                  <Stat label="P/S" value={fmt(a.fundamentals.metrics.ps, 1)} />
                  <Stat label="EV/EBITDA" value={fmt(a.fundamentals.metrics.ev_ebitda, 1)} />
                  <Stat label="ROE" value={`${fmt(a.fundamentals.metrics.roe, 1)}%`} color="#10B981" />
                  <Stat label="ROA/ROIC" value={`${fmt(a.fundamentals.metrics.roic, 1)}%`} color="#10B981" />
                  <Stat label="Net Margin" value={`${fmt(a.fundamentals.metrics.net_margin, 1)}%`} />
                  <Stat label="Rev Growth" value={`${fmt(a.fundamentals.metrics.revenue_growth, 1)}%`} color={chgColor(a.fundamentals.metrics.revenue_growth)} />
                  <Stat label="EPS Growth" value={`${fmt(a.fundamentals.metrics.eps_growth, 1)}%`} color={chgColor(a.fundamentals.metrics.eps_growth)} />
                  <Stat label="Op Margin" value={`${fmt(a.fundamentals.metrics.operating_margin, 1)}%`} />
                </div>
                {(a.fundamentals.beta != null || a.fundamentals.dividend_yield != null || a.fundamentals.analyst_target != null) && (
                  <div className="mt-4 pt-3 border-t border-[#1E293B] flex flex-wrap items-center gap-4 text-xs font-mono text-slate-400">
                    {a.fundamentals.beta != null && <span>Beta <b className="text-slate-200">{fmt(a.fundamentals.beta, 2)}</b></span>}
                    {a.fundamentals.dividend_yield != null && <span>Div Yield <b className="text-slate-200">{fmt(a.fundamentals.dividend_yield, 2)}%</b></span>}
                    {a.fundamentals.analyst_target != null && <span>Analyst TP <b className="text-[#00E5FF]">{fmt(a.fundamentals.analyst_target)}</b></span>}
                  </div>
                )}
                {a.analyst && (
                  <div className="mt-3 pt-3 border-t border-[#1E293B] flex items-center gap-4 text-xs font-mono">
                    <span>Analyst: <b className="text-[#00E5FF]">{a.analyst.consensus}</b></span>
                    <span>Target <b>{fmt(a.analyst.target_price)}</b></span>
                    <span style={{ color: chgColor(a.analyst.implied_upside) }}>{pct(a.analyst.implied_upside)} upside</span>
                    <span className="text-slate-500">{a.analyst.buy}B / {a.analyst.hold}H / {a.analyst.sell}S</span>
                  </div>
                )}
              </Panel>
            ) : (
              <Panel title="Seasonality — Avg Monthly Return" icon={<History size={13} />}>
                <SeasChart data={seasData} cur={seas.current_month} />
              </Panel>
            )}
          </div>

          {a.fundamentals && (
            <Panel title="Seasonality — Avg Monthly Return" icon={<History size={13} />}>
              <SeasChart data={seasData} cur={seas.current_month} />
            </Panel>
          )}
        </div>
      )}

      {tab === "scores" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="Sub-Score Radar" icon={<Sparkles size={13} />}>
            <ResponsiveContainer width="100%" height={340}>
              <RadarChart data={radar} outerRadius="72%">
                <PolarGrid stroke="#1B243B" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Radar dataKey="v" stroke={m.color} fill={m.color} fillOpacity={0.3} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Score Breakdown & Regime Weights" icon={<Gauge size={13} />}>
            <div className="space-y-3">
              {subScoreOrder.map(([k, l]) => (
                <div key={k} data-testid={`subscore-${k}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-mono text-slate-300">{l} <span className="text-slate-600">· w {(a.weights[k] * 100).toFixed(0)}%</span></span>
                    <span className="font-mono font-bold tnum" style={{ color: scoreMeta(a.scores[k]).color }}>{a.scores[k]}</span>
                  </div>
                  <ScoreBar value={a.scores[k]} />
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#1E293B] eyebrow">
              weights are dynamic — tilted for the current regime
            </div>
          </Panel>
        </div>
      )}

      {tab === "why" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="why-score-panel">
          <Panel title={`Positive Drivers (${a.why.positive.length})`} icon={<Sparkles size={13} className="text-[#10B981]" />}>
            <div className="space-y-2">
              {a.why.positive.length ? a.why.positive.map((f, i) => <FactorChip key={i} text={f} positive testid={`why-score-factor-chip-pos-${i}`} />) : <div className="text-xs text-slate-500 font-mono">no dominant positive factors</div>}
            </div>
          </Panel>
          <Panel title={`Negative Drag (${a.why.negative.length})`} icon={<Sparkles size={13} className="text-[#EF4444]" />}>
            <div className="space-y-2">
              {a.why.negative.length ? a.why.negative.map((f, i) => <FactorChip key={i} text={f} positive={false} testid={`why-score-factor-chip-neg-${i}`} />) : <div className="text-xs text-slate-500 font-mono">no dominant negative factors</div>}
            </div>
          </Panel>
        </div>
      )}

      {tab === "analogues" && (
        <Panel title="Historical Analogues — Similar Macro Regimes" icon={<History size={13} />}>
          <div className="eyebrow mb-3">closest historical periods to today's macro tensor, with subsequent SPY forward returns</div>
          <div className="space-y-2" data-testid="analogues-list">
            {analogues.map((an, i) => (
              <div key={i} className="panel-2 rounded-md p-3 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex items-center gap-3 md:w-48">
                  <ScoreGauge value={an.similarity} size={54} thickness={6} label={false} />
                  <div>
                    <div className="font-mono font-bold text-sm">{an.date}</div>
                    <div className="eyebrow">{an.similarity}% similar</div>
                  </div>
                </div>
                <div className="flex-1 grid grid-cols-4 gap-2">
                  {["1M", "3M", "6M", "12M"].map((k) => (
                    <div key={k} className="text-center">
                      <div className="eyebrow">SPY {k}</div>
                      <div className="font-mono font-bold tnum text-sm" style={{ color: chgColor(an.forward_spy[k]) }}>{pct(an.forward_spy[k], 1)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {tab === "corr" && a.correlations && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="asset-correlations">
          {[["60", "60-Day"], ["252", "1-Year"], ["756", "3-Year"], ["1260", "5-Year"]]
            .filter(([w]) => a.correlations[w])
            .map(([w, l]) => (
            <Panel key={w} title={`Intermarket Correlation — ${l}`} icon={<Layers size={13} />}>
              <div className="space-y-1.5">
                {Object.entries(a.correlations[w]).map(([sym, v]) => (
                  <div key={sym} className="flex items-center gap-2">
                    <span className="font-mono text-xs w-14">{sym}</span>
                    <div className="flex-1 h-4 rounded flex items-center px-2" style={{ background: corrColor(v) }}>
                      <span className="text-[10px] font-mono tnum text-slate-100">{fmt(v, 2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}

function SeasChart({ data, cur }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke="#1B243B" vertical={false} />
        <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
        <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} />
        <Tooltip contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono" }} cursor={{ fill: "#ffffff08" }} />
        <ReferenceLine y={0} stroke="#334155" />
        <Bar dataKey="avg" radius={[3, 3, 0, 0]}>
          {data.map((e, i) => (
            <Cell key={i} fill={i + 1 === cur ? "#00E5FF" : e.avg >= 0 ? "#10B981" : "#EF4444"} fillOpacity={i + 1 === cur ? 1 : 0.6} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
